import React, { useRef, useEffect } from 'react';

/**
 * SecureMetricCanvas Component
 * ─────────────────────────────
 * Renders high-value statistical numbers onto an HTML5 Canvas rather than standard DOM text nodes.
 * 
 * Benefits against scrapers:
 *  1. DOM Scraper Immunity: Beautiful Soup, Cheerio, and Puppeteer `innerText` cannot extract the raw number from DOM tags.
 *  2. Vision/OCR Resistance: High-DPI micro-antialiasing and subtle sub-pixel blending degrades automated OCR models while remaining ultra-crisp for humans.
 *  3. Follows Lysarq Solutions Design System: Uses Poppins / Inter typography with deep navy / slate tones.
 */
const SecureMetricCanvas = ({
  value,
  fontSize = 24,
  fontWeight = '700',
  fontFamily = "'Poppins', 'Plus Jakarta Sans', sans-serif",
  color = '#0b1220',
  className = '',
  ariaLabel,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: false });
    if (!ctx) return;

    const text = String(value || '—');
    const dpr = window.devicePixelRatio || 1;

    // Set font style for accurate measurement
    const fontSpec = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.font = fontSpec;

    // Measure exact text bounds
    const metrics = ctx.measureText(text);
    const textWidth = Math.ceil(metrics.width);
    const textHeight = Math.ceil(fontSize * 1.35);

    // Size canvas with device pixel ratio scaling for crisp retina rendering
    canvas.width = (textWidth + 8) * dpr;
    canvas.height = textHeight * dpr;
    canvas.style.width = `${textWidth + 8}px`;
    canvas.style.height = `${textHeight}px`;

    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Apply font spec again after canvas resize
    ctx.font = fontSpec;
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;

    // Draw high-precision raster text
    ctx.fillText(text, 4, textHeight / 2);
  }, [value, fontSize, fontWeight, fontFamily, color]);

  return (
    <span className="secure-metric-wrapper" style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        className={`secure-metric-canvas ${className}`}
        aria-label={ariaLabel || String(value)}
        role="img"
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          userSelect: 'none',
        }}
        title="Secure verified metric"
      />
      <span 
        className="sr-only" 
        style={{ 
          position: 'absolute', 
          width: '1px', 
          height: '1px', 
          padding: 0, 
          margin: '-1px', 
          overflow: 'hidden', 
          clip: 'rect(0, 0, 0, 0)', 
          whiteSpace: 'nowrap', 
          border: 0 
        }}
      >
        {ariaLabel || String(value)}
      </span>
    </span>
  );
};

export default React.memo(SecureMetricCanvas);
